/**
 * Componente DealKanbanBoard - Board Kanban completo para Deals
 * Gerencia drag-and-drop de deals entre colunas e reordenação de colunas
 * Versão: 2.5.0 - Validações ao arrastar para fases ganho/perdido (mesmas regras do modal)
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { 
  SortableContext, 
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove 
} from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CrmDeal, useCrmDeals, DealFilters } from '@/hooks/useCrmDeals';
import { usePipelinePhaseStats } from '@/hooks/usePipelinePhaseStats';
import { useInvalidateKanbanColumns } from '@/hooks/useKanbanColumn';
import { useKanbanColumnCounts, hasActiveFilters } from '@/hooks/useKanbanColumnCounts';
import { useReorderPipelinePhases } from '@/hooks/useReorderPipelinePhases';
import { useCrmDealsRealtime } from '@/hooks/useCrmDealsRealtime';
import { useKanbanUnreadRealtime } from '@/hooks/useKanbanUnreadRealtime';
import { useDealPhases } from '@/hooks/useDealPhases';
import { useCurrentUserSeller } from '@/hooks/useCurrentUserSeller';
import { usePhaseAutomations } from '@/hooks/usePhaseAutomations';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useCrmPhasePermissions } from '@/hooks/useCrmPhasePermissions';
import { supabase } from '@/integrations/supabase/client';
import { DealKanbanColumn } from './DealKanbanColumn';
import { DealCard } from './DealCard';
import { AddPhaseColumn } from './AddPhaseColumn';
import { WinDealCustomerModal } from './WinDealCustomerModal';
import { DealLostPhaseSelectorModal } from './modal/DealLostPhaseSelectorModal';
import { Skeleton } from '@/components/ui/skeleton';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import { AlertTriangle, UserX } from 'lucide-react';

// Logger para o Kanban Board
const logger = createLogger('DealKanban');

// Props do componente
interface DealKanbanBoardProps {
  pipelineId: string;
  /**
   * Workspace ativo do pipeline atual.
   * Usado para resolver permissões granulares por fase via `useCrmPhasePermissions`.
   * Quando ausente, mantém comportamento legado (drag liberado para todos).
   */
  workspaceId?: string | null;
  filters?: DealFilters;
  onAddDeal?: (phaseId: string) => void;
  onEditDeal?: (deal: CrmDeal) => void;
  onViewDeal?: (deal: CrmDeal) => void;
  /** Callback para abrir modal de automações */
  onAutomationClick?: () => void;
  /** Callback para abrir modal de chat com uma conversa específica */
  onOpenChat?: (conversationId: string) => void;
  /** Callback para abrir modal de cliente */
  onViewCustomer?: (customerId: string) => void;
  /** Callback para abrir modal 360º do contato (quando não há cliente).
      v1.9.0: repassa o preview do contato junto ao id */
  onViewContact?: (contactId: string, preview?: {
    id: string;
    name?: string | null;
    phone?: string | null;
    email?: string | null;
    avatar_url?: string | null;
  }) => void;
  /** v2.5.0: Callback para abrir modal de atividade ao clicar no badge de checklist */
  onOpenActivity?: (dealId: string) => void;
}

/**
 * Componente principal do Kanban Board
 * Versão Enterprise com carregamento por coluna e reordenação de colunas
 */
export function DealKanbanBoard({
  pipelineId,
  workspaceId,
  filters,
  onAddDeal,
  onEditDeal,
  onViewDeal,
  onAutomationClick,
  onOpenChat,
  onViewCustomer,
  onViewContact,
  onOpenActivity,
}: DealKanbanBoardProps) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const invalidateColumns = useInvalidateKanbanColumns();
  
  // Verifica se usuário tem vendedor vinculado (para restrição de visualização)
  const { needsSellerLink, hasFullAccess, isLoading: sellerLoading } = useCurrentUserSeller();
  
  // Busca stats das fases via cache (rápido!)
  const { phases, isLoading: statsLoading } = usePipelinePhaseStats(pipelineId);
  
  // Contagens filtradas - só busca quando há filtros ativos (v2.6.0)
  const { countsMap: filteredCounts, filtersActive } = useKanbanColumnCounts(pipelineId, filters);
  
  // Real-time orientado a eventos - atualiza cache sem re-fetch
  useCrmDealsRealtime(pipelineId);
  
  // Realtime centralizado para unread counts - 1 canal por pipeline (sem RPCs)
  const { realtimeUnreadUpdates } = useKanbanUnreadRealtime(pipelineId);
  
  // Hook para detectar automações por fase (indicador visual ⚡)
  const { hasAutomation, automationCount } = usePhaseAutomations(pipelineId);
  
  // ==========================================================================
  // CONTROLE DE PERMISSÃO PARA ÍCONES DE AUTOMAÇÃO
  // Admin: sempre visível | Supervisor: se tiver acesso a 'flows' ou 'routing'
  // Agent: nunca visível
  // ==========================================================================
  const { isAdmin, canView, canCreate, canEdit } = usePermissions();
  // v - Permissão granular para criar nova fase no Kanban (independente de `crm_deals`)
  const canCreatePhase = canCreate('crm_phases');
  // Permissão legada de edição de deal (usada quando granular OFF)
  const canEditDealsLegacy = canEdit('crm_deals');
  const canViewAutomations = useMemo(() => {
    if (isAdmin) return true;
    return canView('flows') || canView('routing');
  }, [isAdmin, canView]);

  // ==========================================================================
  // PERMISSÕES GRANULARES POR FASE (workspace-scoped)
  // Resolve `can_change_phase` por fase para habilitar/desabilitar o drag.
  // - Admin: sempre pode (isAdmin=true via hook).
  // - Granular OFF (workspace sem matriz): mantém comportamento legado.
  // - Granular ON: só pode mover se a fase de origem E destino tiverem `can_change_phase`.
  // ==========================================================================
  const {
    isAdmin: isPhaseAdmin,
    isGranularEnabled,
    canDoOnPhase,
  } = useCrmPhasePermissions(workspaceId);

  /** Helper: o usuário pode mover (entrar/sair) cards desta fase? */
  const canMovePhase = useCallback(
    (phaseId: string | null | undefined): boolean => {
      if (isPhaseAdmin) return true;
      if (!isGranularEnabled) return true;
      return canDoOnPhase(phaseId, 'can_change_phase');
    },
    [isPhaseAdmin, isGranularEnabled, canDoOnPhase]
  );

  /**
   * Helpers: resolvem permissões granulares por fase para o MENU do card,
   * espelhando exatamente a regra do modal:
   *  - Admin → libera tudo.
   *  - Granular OFF → fallback para a permissão legada `canEdit('crm_deals')`.
   *  - Granular ON → consulta a matriz por ação (`can_edit` / `can_change_phase` / `can_reopen`).
   */
  const canEditDealOnPhase = useCallback(
    (phaseId: string | null | undefined): boolean => {
      if (isPhaseAdmin) return true;
      if (!isGranularEnabled) return canEditDealsLegacy;
      return canDoOnPhase(phaseId, 'can_edit');
    },
    [isPhaseAdmin, isGranularEnabled, canDoOnPhase, canEditDealsLegacy]
  );

  const canChangePhaseOnPhase = useCallback(
    (phaseId: string | null | undefined): boolean => {
      if (isPhaseAdmin) return true;
      if (!isGranularEnabled) return canEditDealsLegacy;
      return canDoOnPhase(phaseId, 'can_change_phase');
    },
    [isPhaseAdmin, isGranularEnabled, canDoOnPhase, canEditDealsLegacy]
  );

  const canReopenDealOnPhase = useCallback(
    (phaseId: string | null | undefined): boolean => {
      if (isPhaseAdmin) return true;
      if (!isGranularEnabled) return canEditDealsLegacy;
      return canDoOnPhase(phaseId, 'can_reopen');
    },
    [isPhaseAdmin, isGranularEnabled, canDoOnPhase, canEditDealsLegacy]
  );

  
  // Mutations do hook de deals (para mover, marcar ganho/perda)
  // NOTA: enabled: false desabilita a query automática - usamos RPC por coluna
  const { moveDealToPhase, markAsWon, markAsLost, reopenDeal } = useCrmDeals(
    { pipeline_id: pipelineId },
    { enabled: false } // Não carrega todos os deals - Kanban usa RPC get_kanban_column
  );

  // Mutation para reordenar colunas/fases
  const reorderPhases = useReorderPipelinePhases(pipelineId);

  // Hook para criar fases
  const { createPhase } = useDealPhases(pipelineId);

  // Handler para adicionar nova fase com todos os parâmetros
  const handleAddPhase = async (
    name: string, 
    color: string, 
    probability: number, 
    isWon: boolean, 
    isLost: boolean
  ) => {
    await createPhase.mutateAsync({ 
      name, 
      color, 
      probability_percent: probability,
      is_won: isWon,
      is_lost: isLost
    });
  };

  // Estado para drag overlay
  const [activeDeal, setActiveDeal] = useState<CrmDeal | null>(null);
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null);
  
  // Estado local para reordenação visual em tempo real durante drag de colunas
  const [localPhaseOrder, setLocalPhaseOrder] = useState<typeof phases | null>(null);
  
  // Fases exibidas: usa ordem local durante drag, senão a original
  const displayPhases = localPhaseOrder ?? phases;
  
  // Estado para modal de ganhar negócio com cadastro de cliente
  const [winDealModalOpen, setWinDealModalOpen] = useState(false);
  const [winDealData, setWinDealData] = useState<{ dealId: string; contactName?: string; dealTitle?: string } | null>(null);

  // v2.6.0: Estado para modal de seleção de fase de perda (múltiplos motivos)
  const [lostSelectorOpen, setLostSelectorOpen] = useState(false);
  const [lostSelectorDeal, setLostSelectorDeal] = useState<CrmDeal | null>(null);

  // IDs das colunas para SortableContext (prefixados para evitar conflito com deals)
  const columnIds = useMemo(() => 
    displayPhases.map(p => `column-${p.phase_id}`), 
    [displayPhases]
  );

  // Sensores para drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Handler de início do drag - USA active.data.current diretamente (sem cache!)
  const handleDragStart = (event: DragStartEvent) => {
    const activeData = event.active.data.current;
    
    logger.debug('handleDragStart:', { 
      id: event.active.id, 
      type: activeData?.type,
      phaseId: activeData?.phaseId 
    });

    // Verifica tipo via data.current (fonte confiável)
    if (activeData?.type === 'column') {
      setActiveColumnId(activeData.phaseId);
      setActiveDeal(null);
      // Inicializa ordem local para reordenação visual em tempo real
      setLocalPhaseOrder([...phases]);
      return;
    }

    // É um deal - pega o deal diretamente do data (sem buscar no cache!)
    if (activeData?.type === 'deal' && activeData?.deal) {
      setActiveColumnId(null);
      setActiveDeal(activeData.deal as CrmDeal);
      return;
    }

    // Fallback: limpa estados
    setActiveColumnId(null);
    setActiveDeal(null);
  };

  // Handler de drag over - reordena colunas visualmente em tempo real
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Só processa se o active é uma coluna
    if (activeData?.type !== 'column') return;

    const activePhaseId = activeData.phaseId;
    // Extrai phaseId do over independente do tipo (pode ser deal, phase ou column)
    const overPhaseId = overData?.type === 'column' 
      ? overData.phaseId 
      : overData?.phaseId || (over.id as string).replace('column-', '');

    if (!overPhaseId || activePhaseId === overPhaseId) return;

    setLocalPhaseOrder((prev) => {
      if (!prev) return prev;
      const oldIndex = prev.findIndex(p => p.phase_id === activePhaseId);
      const newIndex = prev.findIndex(p => p.phase_id === overPhaseId);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  // Handler de fim do drag - USA data.current diretamente (sem cache!)
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const activeData = active.data.current;
    const overData = over?.data.current;
    
    logger.debug('handleDragEnd:', { 
      activeId: active.id, 
      activeType: activeData?.type,
      overId: over?.id,
      overType: overData?.type 
    });

    // Captura ordem visual final ANTES de limpar estado
    const finalOrder = localPhaseOrder ? [...localPhaseOrder] : null;
    
    // Limpa estados
    setActiveDeal(null);
    setActiveColumnId(null);
    setLocalPhaseOrder(null);
    
    if (!over) return;

    const overId = over.id as string;

    // ==========================================================================
    // REORDENAÇÃO DE COLUNAS - usa finalOrder capturado do drag visual
    // ==========================================================================
    if (activeData?.type === 'column' && finalOrder) {
      // Verifica se a ordem realmente mudou comparando com phases original
      const orderChanged = finalOrder.some((p, i) => p.phase_id !== phases[i]?.phase_id);
      
      if (orderChanged) {
        logger.info('Persistindo reordenação de colunas (visual → banco)');
        const phaseOrders = finalOrder.map((p, index) => ({
          phase_id: p.phase_id,
          sort_order: index
        }));
        reorderPhases.mutate(phaseOrders);
      }
      return;
    }

    // ==========================================================================
    // MOVIMENTO DE DEALS
    // ==========================================================================
    if (activeData?.type !== 'deal') return;

    const dealId = active.id as string;
    const currentPhaseId = activeData.phaseId as string;
    
    // Determina fase de destino via data.current (prioridade) ou overId
    let targetPhaseId: string | null = null;

    if (overData?.type === 'deal' && overData?.phaseId) {
      // Soltou sobre outro deal - usa a fase desse deal
      targetPhaseId = overData.phaseId;
    } else if (overData?.type === 'phase' && overData?.phaseId) {
      // Soltou sobre o droppable da coluna
      targetPhaseId = overData.phaseId;
    } else if (overId.startsWith('column-')) {
      // Soltou sobre coluna (sortable)
      targetPhaseId = overId.replace('column-', '');
    } else {
      // Verifica se overId é uma fase direta
      const isPhase = phases.some(p => p.phase_id === overId);
      if (isPhase) {
        targetPhaseId = overId;
      }
    }

    // Valida se conseguiu determinar a fase de destino
    if (!targetPhaseId) {
      logger.warn('Não foi possível determinar fase de destino:', { overId, overData });
      return;
    }

    // Valida se é uma fase válida
    const isValidPhase = phases.some(p => p.phase_id === targetPhaseId);
    if (!isValidPhase) {
      logger.warn('Fase de destino inválida:', targetPhaseId);
      return;
    }

    // ==========================================================================
    // GUARD UX: Permissão granular `can_change_phase` na fase de origem/destino
    // Server-side já recusaria, mas aqui evitamos a chamada e damos feedback claro.
    // ==========================================================================
    if (!canMovePhase(currentPhaseId) || !canMovePhase(targetPhaseId)) {
      logger.warn('Drag bloqueado por permissão granular de fase', {
        currentPhaseId,
        targetPhaseId,
      });
      toast.error('Sem permissão para mover negócios desta/para esta fase');
      return;
    }

    // Move apenas se mudou de fase
    if (currentPhaseId && currentPhaseId !== targetPhaseId) {
      logger.info(`Movendo deal ${dealId} de ${currentPhaseId} para ${targetPhaseId}`);
      
      // Busca dados da fase de destino
      const targetPhase = phases.find(p => p.phase_id === targetPhaseId);
      
      // ==========================================================================
      // UNIFICAÇÃO via RPC `move_deal_to_phase` (fonte única de verdade)
      //
      // A RPC já cobre:
      //  - open → is_won  : seta status='won' + actual_close_date=hoje
      //  - open → is_lost : seta status='lost' + actual_close_date=hoje
      //  - won  → is_lost : RAISE EXCEPTION (check_violation) — exige reabrir
      //  - lost → is_won  : RAISE EXCEPTION (check_violation) — exige reabrir
      //  - won/lost → qualquer outra fase: preserva status e actual_close_date
      //
      // Aqui mantemos APENAS a pré-validação UX para o caminho open → is_won:
      //  1) Deal precisa de pelo menos 1 produto/serviço.
      //  2) Se o contato não tem cliente vinculado, abrimos o modal que chama
      //     `mark_deal_as_won_with_customer` (com os dados cadastrais).
      // Para deals já ganhos/perdidos, pulamos a pré-validação (cross-pipeline).
      // ==========================================================================
      try {
        // Busca status atual do deal — necessário para decidir se aplicamos
        // pré-validações de cliente/itens (apenas para deals em aberto).
        const { data: dealCurrent, error: dealStatusError } = await supabase
          .from('crm_deals')
          .select('id, status, contact_id, title')
          .eq('id', dealId)
          .single();

        if (dealStatusError || !dealCurrent) {
          logger.error('Erro ao buscar status do deal:', dealStatusError?.message);
          toast.error('Erro ao processar negócio');
          return;
        }

        // Pré-validação UX para open → is_won (mesma regra do botão "Ganho")
        if (targetPhase?.is_won && dealCurrent.status === 'open') {
          logger.info('open → is_won: aplicando pré-validações');

          // 1) Itens obrigatórios
          const { count: itemsCount } = await supabase
            .from('crm_deal_items')
            .select('id', { count: 'exact', head: true })
            .eq('deal_id', dealId);

          if (!itemsCount || itemsCount === 0) {
            toast.error('Adicione ao menos um produto/serviço antes de marcar como ganho');
            return;
          }

          // 2) Cliente vinculado ao contato (se não houver, abre modal)
          if (dealCurrent.contact_id) {
            const { data: customer } = await supabase
              .from('crm_customers')
              .select('id')
              .eq('main_contact_id', dealCurrent.contact_id)
              .eq('tenant_id', currentTenant?.id || '')
              .maybeSingle();

            if (!customer) {
              // Busca nome do contato para exibir no modal
              const { data: contact } = await supabase
                .from('contacts')
                .select('name')
                .eq('id', dealCurrent.contact_id)
                .maybeSingle();

              logger.info('Contato sem cliente vinculado, abrindo modal de cadastro');
              setWinDealData({
                dealId,
                contactName: contact?.name || undefined,
                dealTitle: dealCurrent.title,
              });
              setWinDealModalOpen(true);
              return; // Modal chama mark_deal_as_won_with_customer
            }
          }
        }

        // Para todos os demais casos (incluindo open → is_won com cliente já
        // existente, won/lost → qualquer fase, fases intermediárias), a RPC
        // `move_deal_to_phase` é o único ponto de mutação.
        await moveDealToPhase.mutateAsync({ dealId, newPhaseId: targetPhaseId });
        invalidateColumns(pipelineId);
      } catch (error) {
        // Erros amigáveis já são tratados em moveDealToPhase.onError; aqui
        // apenas logamos para diagnóstico — o toast genérico vem da mutation.
        logger.error('Erro ao mover deal via RPC unificada:', error);
      }
    }
  };

  /**
   * Handler para marcar deal como ganho
   * v2.0.0: Verifica se há produtos/serviços antes de prosseguir
   * Verifica se contato tem cliente vinculado antes de prosseguir
   * Se não tiver, abre modal para cadastrar dados do cliente
   */
  const handleMarkWon = async (deal: CrmDeal) => {
    // v2.0.0: Verifica se deal possui itens (produtos/serviços)
    const { count: itemsCount } = await supabase
      .from('crm_deal_items')
      .select('id', { count: 'exact', head: true })
      .eq('deal_id', deal.id);
    
    if (!itemsCount || itemsCount === 0) {
      toast.error('Adicione ao menos um produto/serviço antes de marcar como ganho');
      return;
    }

    // Verifica se o contato do deal tem cliente vinculado
    if (deal.contact_id) {
      const { data: customer } = await supabase
        .from('crm_customers')
        .select('id')
        .eq('main_contact_id', deal.contact_id)
        .eq('tenant_id', currentTenant?.id || '')
        .maybeSingle();
      
      if (!customer) {
        // Sem cliente - abre modal para cadastrar dados obrigatórios
        logger.info('Contato sem cliente vinculado, abrindo modal de cadastro');
        setWinDealData({
          dealId: deal.id,
          contactName: deal.contact?.name || undefined,
          dealTitle: deal.title,
        });
        setWinDealModalOpen(true);
        return;
      }
    }
    
    // Cliente existe ou não tem contato - marca direto como ganho
    logger.info('Marcando deal como ganho direto');
    await markAsWon.mutateAsync(deal.id);
    invalidateColumns(pipelineId);
  };

  // v2.6.0: handleMarkLost considera múltiplos motivos de perda no pipeline
  // - 0 fases lost → toast de erro (configuração ausente).
  // - 1 fase lost → move direto.
  // - 2+ fases lost → abre modal para o usuário escolher o motivo.
  const handleMarkLost = async (deal: CrmDeal) => {
    const lostPhases = phases.filter((p) => p.is_lost);
    if (lostPhases.length === 0) {
      toast.error('Configure uma fase de perda no pipeline antes de marcar como perdido');
      return;
    }
    if (lostPhases.length === 1) {
      await markAsLost.mutateAsync({ dealId: deal.id, lossReason: undefined, targetPhaseId: lostPhases[0].phase_id });
      invalidateColumns(pipelineId);
      return;
    }
    // Múltiplas fases: abre modal de seleção
    setLostSelectorDeal(deal);
    setLostSelectorOpen(true);
  };

  // Confirmação do modal de motivos
  const handleLostSelectorConfirm = async (phaseId: string) => {
    if (!lostSelectorDeal) return;
    await markAsLost.mutateAsync({ dealId: lostSelectorDeal.id, lossReason: undefined, targetPhaseId: phaseId });
    invalidateColumns(pipelineId);
    setLostSelectorOpen(false);
    setLostSelectorDeal(null);
  };

  const handleReopen = async (deal: CrmDeal) => {
    await reopenDeal.mutateAsync(deal.id);
    invalidateColumns(pipelineId);
  };

  /**
   * Callback de sucesso ao ganhar negócio via modal
   */
  const handleWinDealSuccess = () => {
    setWinDealModalOpen(false);
    setWinDealData(null);
    invalidateColumns(pipelineId);
  };

  // Loading state
  if (statsLoading || sellerLoading) {
    return (
      <div className="flex gap-4 p-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="min-w-[280px] space-y-3">
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  // Alerta para agentes sem vendedor vinculado (sem acesso aos deals)
  if (needsSellerLink) {
    return (
      <div className="flex items-center justify-center p-8 h-64">
        <Alert variant="destructive" className="max-w-lg">
          <UserX className="h-5 w-5" />
          <AlertTitle className="flex items-center gap-2">
            Acesso Restrito
          </AlertTitle>
          <AlertDescription className="mt-2 space-y-2">
            <p>
              Você não possui um vendedor vinculado ao seu usuário, portanto não pode 
              visualizar os negócios do CRM.
            </p>
            <p className="text-sm opacity-80">
              Entre em contato com um administrador para vincular seu usuário a um 
              vendedor no módulo de Vendedores.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Empty state - mostra opção de criar primeira fase
  if (!phases || phases.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-6 p-8 h-64">
        <div className="text-center space-y-2">
          <p className="text-muted-foreground">
            Nenhuma fase configurada neste pipeline.
          </p>
          <p className="text-sm text-muted-foreground/70">
            Crie a primeira fase para começar a organizar seus negócios.
          </p>
        </div>
        
        {/* Coluna para adicionar nova fase (oculta sem permissão `crm_phases.can_create`) */}
        <AddPhaseColumn 
          onAdd={handleAddPhase} 
          isLoading={createPhase.isPending}
          canCreate={canCreatePhase}
        />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {/* 
        Scroll Surface - Container que "segura" o scroll vertical
        Impede que scroll em área vazia do Kanban propague para a página
        overscroll-contain = bloqueia scroll chaining vertical
      */}
      <div 
        className="h-full min-h-0 min-w-0 w-full max-w-full overflow-hidden overscroll-contain"
        onWheel={(e) => {
          // Bloqueia scroll vertical em área vazia do Kanban (não rola nada)
          // Permite apenas scroll horizontal (deltaX)
          if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            e.stopPropagation();
          }
        }}
      >
        {/* ScrollIndicators + ScrollArea horizontal com anti-scroll-chaining */}
        <ScrollIndicators className="h-full">
          <ScrollArea 
            className="w-full max-w-full h-full"
            preventScrollChaining
            touchAxis="pan-x"
          >
            {/* SortableContext para reordenação de colunas */}
            <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
              {/* 
                Container das colunas com altura calculada explícita
                calc(100vh - 220px) = viewport - header da página - tabs - padding
                Isso garante que as colunas tenham altura definida para o ScrollArea interno funcionar
              */}
              <div className="flex gap-4 p-4 h-[calc(100vh-220px)]">
                {displayPhases.map((phase) => {
                  // v2.6.0: Usa contagens filtradas quando há filtros ativos
                  const filteredData = filtersActive ? filteredCounts.get(phase.phase_id) : null;
                  const displayDealCount = filtersActive ? (filteredData?.dealCount ?? 0) : phase.deal_count;
                  const displayTotalValue = filtersActive ? (filteredData?.totalValue ?? 0) : phase.total_value;

                  return (
                  <DealKanbanColumn
                    key={phase.phase_id}
                    pipelineId={pipelineId}
                    phaseId={phase.phase_id}
                    phaseName={phase.phase_name}
                    phaseColor={phase.phase_color}
                    dealCount={displayDealCount}
                    totalValue={displayTotalValue}
                    isWon={phase.is_won}
                    isLost={phase.is_lost}
                    filters={filters}
                    onAddDeal={onAddDeal}
                    onEditDeal={onEditDeal}
                    onViewDeal={onViewDeal}
                    onMarkWon={handleMarkWon}
                    onMarkLost={handleMarkLost}
                    onReopen={handleReopen}
                    // Ícones de automação: só aparece se usuário tem permissão
                    hasAutomation={canViewAutomations && hasAutomation(phase.phase_id)}
                    automationCount={canViewAutomations ? automationCount(phase.phase_id) : 0}
                    onAutomationClick={canViewAutomations ? onAutomationClick : undefined}
                    onOpenChat={onOpenChat}
                    // Realtime unread centralizado: Map de atualizações sem RPC
                    realtimeUnreadUpdates={realtimeUnreadUpdates}
                    // Cliente: passa callback para abrir modal de cliente
                    onViewCustomer={onViewCustomer}
                    // Contato: abre o modal 360º quando o negócio não tem cliente
                    onViewContact={onViewContact}
                    // Permissão granular `can_change_phase` por fase (workspace-scoped)
                    canDrag={canMovePhase(phase.phase_id)}
                    // Permissões granulares por fase aplicadas ao menu do card (mesmas do modal)
                    canEditDeal={canEditDealOnPhase(phase.phase_id)}
                    canChangePhase={canChangePhaseOnPhase(phase.phase_id)}
                    canReopenDeal={canReopenDealOnPhase(phase.phase_id)}
                    // v2.5.0: Abrir modal de atividade pelo badge de checklist
                    onOpenActivity={onOpenActivity}
                  />
                  );
                })}
                
                {/* Coluna para adicionar nova fase (oculta sem permissão `crm_phases.can_create`) */}
                <AddPhaseColumn 
                  onAdd={handleAddPhase} 
                  isLoading={createPhase.isPending}
                  canCreate={canCreatePhase}
                />
              </div>
            </SortableContext>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </ScrollIndicators>
      </div>
      
      {/* Overlay para deal sendo arrastado - visual premium com animação suave */}
      <DragOverlay 
        dropAnimation={{
          duration: 200,
          easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
        }}
      >
        {activeDeal && (
          <div className="rotate-2 scale-105 shadow-2xl">
            {/* batchTagAssignments=[] evita fallback de query individual durante drag */}
            <DealCard deal={activeDeal} batchTagAssignments={[]} />
          </div>
        )}
        {activeColumnId && (() => {
          // Busca dados da fase sendo arrastada para preview realista
          const draggedPhase = phases.find(p => p.phase_id === activeColumnId);
          const phaseColor = draggedPhase?.phase_color || '#6b7280';
          const hexToRgba = (hex: string, alpha: number) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
          };
          return (
            <div className="w-[300px] h-64 bg-muted/80 rounded-lg border border-border/50 shadow-2xl rotate-1 overflow-hidden flex flex-col">
              {/* Header realista */}
              <div 
                className="p-3 border-b border-border/50 rounded-t-lg"
                style={{ backgroundColor: hexToRgba(phaseColor, 0.15) }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: phaseColor }} />
                  <span className="font-medium text-sm truncate">{draggedPhase?.phase_name || 'Coluna'}</span>
                  <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                    {draggedPhase?.deal_count ?? 0}
                  </span>
                </div>
              </div>
              {/* Cards placeholder */}
              <div className="p-2 space-y-2 flex-1">
                <div className="h-16 bg-background/60 rounded-md border border-border/30 animate-pulse" />
                <div className="h-16 bg-background/40 rounded-md border border-border/20 animate-pulse" />
              </div>
            </div>
          );
        })()}
      </DragOverlay>

      {/* Modal de Ganhar Negócio com Cadastro de Cliente */}
      {winDealData && (
        <WinDealCustomerModal
          dealId={winDealData.dealId}
          contactName={winDealData.contactName}
          dealTitle={winDealData.dealTitle}
          open={winDealModalOpen}
          onOpenChange={setWinDealModalOpen}
          onSuccess={handleWinDealSuccess}
        />
      )}

      {/* v2.6.0: Modal de seleção de fase de perda (múltiplos motivos) */}
      <DealLostPhaseSelectorModal
        open={lostSelectorOpen}
        onOpenChange={(open) => {
          setLostSelectorOpen(open);
          if (!open) setLostSelectorDeal(null);
        }}
        lostPhases={phases.filter((p) => p.is_lost).map((p) => ({
          phase_id: p.phase_id,
          phase_name: p.phase_name,
          phase_color: p.phase_color,
        }))}
        onConfirm={handleLostSelectorConfirm}
        isPending={markAsLost.isPending}
      />
    </DndContext>
  );
}
